import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, User, Phone } from "lucide-react";
import { FAQS, SUPPORT_EMAIL, WHATSAPP_NUMBER, WHATSAPP_DISPLAY, CALL_NUMBER, CALL_DISPLAY } from "../data/site";

const QUICK = [
  { q: "How do I become a member?", key: "member" },
  { q: "What packages are there?", key: "packages" },
  { q: "What is the only rule?", key: "rule" },
  { q: "How do I pay?", key: "pay" },
  { q: "Talk to a human", key: "human" },
];

const ANSWERS = {
  member: "Easy! Click 'Become a member', register, choose a package (Basic R10, Standard R200, Premium R500 or Pro), and start your monthly contributions. 💚",
  packages: "We have 4: Basic (from R10, learners), Standard (from R200, students), Premium (from R500, workers) and Pro (10% of salary). You can always pay more than the minimum.",
  rule: "Only one rule: contribute monthly for at least 6 months before requesting assistance. After that, as long as there's balance, qualified requests get a YES — no fine print.",
  pay: "Pay by EFT or bank deposit into our FNB account, then upload your proof of payment. Use your initials and surname as the reference.",
  human: `__HUMAN__`,
};

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi! 👋 I'm the GDF assistant. How can I help you today? Pick a question below." },
  ]);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const answer = (key, label) => {
    setMessages((m) => [...m, { from: "user", text: label }]);
    setTimeout(() => {
      if (key === "human") {
        setMessages((m) => [...m, { from: "bot", human: true, text: "No problem — let's connect you to a real person:" }]);
      } else {
        setMessages((m) => [...m, { from: "bot", text: ANSWERS[key] }]);
      }
    }, 300);
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
        aria-label="Open chat"
      >
        {open ? <X /> : <MessageCircle />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden gdf-fade-up" data-testid="chatbot-window">
          <div className="gdf-navy-bg text-white px-4 py-3 flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-emerald-500 flex items-center justify-center"><MessageCircle className="w-4 h-4" /></div>
            <div>
              <p className="font-semibold text-sm font-head">GDF Assistant</p>
              <p className="text-[11px] text-emerald-300">Always here to help</p>
            </div>
          </div>

          <div className="h-72 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.from === "user" ? "bg-emerald-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                  {m.text}
                  {m.human && (
                    <div className="mt-3 flex flex-col gap-2">
                      <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" data-testid="chatbot-whatsapp" className="flex items-center gap-2 bg-emerald-600 text-white px-3 py-2 rounded-lg text-xs font-medium">
                        <MessageCircle className="w-4 h-4" /> WhatsApp {WHATSAPP_DISPLAY}
                      </a>
                      <a href={`tel:${CALL_NUMBER}`} data-testid="chatbot-call" className="flex items-center gap-2 bg-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-medium">
                        <Phone className="w-4 h-4" /> Call {CALL_DISPLAY}
                      </a>
                      <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="chatbot-email" className="flex items-center gap-2 bg-[#0A192F] text-white px-3 py-2 rounded-lg text-xs font-medium">
                        <User className="w-4 h-4" /> Email support
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="px-3 py-3 border-t border-slate-200 bg-white flex flex-wrap gap-2">
            {QUICK.map((item) => (
              <button key={item.key} onClick={() => answer(item.key, item.q)} data-testid={`chatbot-quick-${item.key}`} className="text-xs bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-600 px-3 py-1.5 rounded-full border border-slate-200 transition-colors">
                {item.q}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
